import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { addRoleMenu } from "helpers/backend_helper";
import { Loader2 } from "lucide-react";

interface BulkRoleMenuFormProps {
    onSave: (data: any[]) => void;
    onHide: () => void;
    data: any;
    menus: any[];
    roles: any[];
}

interface BulkRoleMenuFormValues {
    roleId: number;
    selectedMenus: number[];
    isActive: boolean;
}

const BulkRoleMenuForm = ({ onSave, onHide, data, menus, roles }: BulkRoleMenuFormProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const validation = useFormik<BulkRoleMenuFormValues>({
        enableReinitialize: true,
        initialValues: {
            roleId: data?.roleId || "",
            selectedMenus: [],
            isActive: true
        },
        validationSchema: Yup.object({
            roleId: Yup.number().required("Role is required"),
            selectedMenus: Yup.array().min(1, "Please select at least one menu"),
            isActive: Yup.boolean()
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const promises = values.selectedMenus.map(menuId => 
                    addRoleMenu({
                        roleId: values.roleId,
                        menuId,
                        isActive: values.isActive
                    })
                );
                
                const responses = await Promise.all(promises);
                const successfulResponses = responses.filter(response => response?.data?.success);
                
                if (successfulResponses.length > 0) {
                    onSave(successfulResponses.map(response => response.data.data));
                }
            } catch (error) {
                console.error("Error saving role menus:", error);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleMenuToggle = (menuId: number) => {
        const currentSelected = validation.values.selectedMenus;
        const newSelected = currentSelected.includes(menuId)
            ? currentSelected.filter(id => id !== menuId)
            : [...currentSelected, menuId];
        
        validation.setFieldValue('selectedMenus', newSelected);
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h5 className="text-16">Add Role Menus</h5>
                <button
                    type="button"
                    className="text-slate-400 hover:text-slate-500"
                    onClick={onHide}
                >
                    <span className="sr-only">Close</span>
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <form onSubmit={validation.handleSubmit}>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="roleId" className="inline-block mb-2 text-base font-medium">
                            Role
                        </label>
                        <select
                            id="roleId"
                            name="roleId"
                            className="form-select border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.roleId}
                            disabled={!!data?.id}
                        >
                            <option value="">Select Role</option>
                            {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                    {role.name}
                                </option>
                            ))}
                        </select>
                        {validation.touched.roleId && validation.errors.roleId && (
                            <div className="mt-1 text-sm text-red-500">
                                {validation.errors.roleId.toString()}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="inline-block mb-2 text-base font-medium">
                            Select Menus
                        </label>
                        <div className="max-h-[300px] overflow-y-auto border border-slate-200 dark:border-zink-500 rounded-md p-4">
                            <div className="space-y-2">
                                {menus.map((menu) => (
                                    <div key={menu.id} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id={`menu-${menu.id}`}
                                            className="size-4 border rounded-sm appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                                            checked={validation.values.selectedMenus.includes(menu.id)}
                                            onChange={() => handleMenuToggle(menu.id)}
                                        />
                                        <label
                                            htmlFor={`menu-${menu.id}`}
                                            className="text-sm cursor-pointer"
                                        >
                                            {menu.name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {validation.touched.selectedMenus && validation.errors.selectedMenus && (
                            <div className="mt-1 text-sm text-red-500">
                                {validation.errors.selectedMenus.toString()}
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                            Status
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="isActive"
                                name="isActive"
                                className="size-4 border rounded-sm appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                                onChange={validation.handleChange}
                                checked={validation.values.isActive}
                            />
                            <label htmlFor="isActive" className="inline-block text-base font-medium align-middle cursor-pointer">
                                Active
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button
                        type="button"
                        className="text-slate-500 btn bg-slate-200 border-slate-200 hover:text-white hover:bg-slate-300 hover:border-slate-300 focus:text-white focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:text-zink-200 dark:border-zink-600 dark:hover:border-zink-500 dark:focus:border-zink-500 dark:focus:ring-zink-200/50 dark:active:bg-zink-500 dark:active:border-zink-500"
                        onClick={onHide}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BulkRoleMenuForm; 